import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Footer } from "../../App";
import { useTheme } from "../../contexts/ThemeContext";
import { motion } from "framer-motion";

interface ApiErrorFallbackProps {
  errorCode: number;
  resetError: () => void;
}

const ApiErrorFallback: React.FC<ApiErrorFallbackProps> = ({
  errorCode,
  resetError,
}) => {
  const { theme } = useTheme();
  const [errorTitle, setErrorTitle] = useState(
    "Service Temporarily Unavailable"
  );
  const [errorMessage, setErrorMessage] = useState(
    "We're currently experiencing some technical difficulties with our servers. Our team has been notified and is working to resolve the issue as quickly as possible."
  );

  useEffect(() => {
    // Set appropriate error messages based on error code
    switch (errorCode) {
      case 502:
        setErrorTitle("Bad Gateway");
        setErrorMessage(
          "We're having trouble connecting to our services. Please try again in a few moments."
        );
        break;
      case 503:
        setErrorTitle("Service Unavailable");
        setErrorMessage(
          "Our service is temporarily unavailable. We're working to restore it as quickly as possible."
        );
        break;
      case 504:
        setErrorTitle("Gateway Timeout");
        setErrorMessage(
          "The request took too long to process. Please try again in a few moments."
        );
        break;
      default:
        setErrorTitle("Service Temporarily Unavailable");
        setErrorMessage(
          "We're currently experiencing some technical difficulties with our servers. Our team has been notified and is working to resolve the issue as quickly as possible."
        );
    }
  }, [errorCode]);

  return (
    <div className="flex flex-col min-h-screen bg-[color:var(--background)]">
      {/* Header - Mimicking the App.tsx header */}
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-knack-dark-800 backdrop-blur-[2px]"
      >
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-14">
            <Link
              to="/dashboard"
              className="text-[var(--color-light)] hover:text-[var(--color-accent)] transition-colors"
            >
              <h1 className="flex items-center">
                <img
                  src="/assets/ukti-logo-with-text-white.png"
                  alt="Ukti.ai"
                  className="h-10 sm:h-12 md:h-12 w-auto object-contain max-w-none"
                />
              </h1>
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col pt-16 sm:pt-20 md:pt-20">
        <div className="flex-1 flex flex-col items-center justify-center px-4 text-center py-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8 text-[color:var(--accent)]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-24 w-24 mx-auto"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold mb-4 text-[color:var(--foreground)]"
          >
            {errorTitle}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="text-lg mb-8 text-[color:var(--foreground-muted)] max-w-md"
          >
            {errorMessage}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="text-sm mb-8 text-[color:var(--foreground-muted)]"
          >
            Error Code: {errorCode}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.3 }}
            className="flex space-x-4"
          >
            <button
              onClick={resetError}
              className="px-6 py-3 bg-[color:var(--accent)] text-white rounded-md hover:bg-[color:var(--accent)]/90 transition-all duration-300"
            >
              Try Again
            </button>

            <Link
              to="/dashboard"
              className="px-6 py-3 border border-[color:var(--accent)] text-[color:var(--accent)] rounded-md hover:bg-[color:var(--accent)]/10 transition-all duration-300"
            >
              Go to Dashboard
            </Link>
          </motion.div>
        </div>
      </main>

      {/* Footer from App.tsx */}
      <Footer />
    </div>
  );
};

export default ApiErrorFallback;
